import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Wallet, Gift, Package, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout, { NavCard } from '../components/DashboardLayout';
import { usePerms } from '../permissions/PermissionsContext';

export default function CustomerPlatformFinance() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const items = [
    {
      id: 'financial-management',
      titleAr: 'الإدارة المالية',
      titleEn: 'Financial Management',
      descAr: 'إدارة الحسابات والمعاملات المالية',
      descEn: 'Manage accounts and financial transactions',
      icon: CreditCard,
      route: '/dashboard/finance/cloudgm/customers/management',
      color: '#10b981',
      screenCode: 'CGM-CUST-FIN-01',
    },
    {
      id: 'expense-control',
      titleAr: 'التحكم بنقاط الولاء',
      titleEn: 'Loyalty Points Control',
      descAr: 'إدارة نظام نقاط الولاء والمكافآت',
      descEn: 'Manage loyalty points system and rewards',
      icon: Gift,
      route: '/dashboard/finance/cloudgm/customers/loyalty',
      color: '#F59E0B',
      screenCode: 'CGM-CUST-FIN-02',
    },
    {
      id: 'financial-materials',
      titleAr: 'الموارد المالية',
      titleEn: 'Financial Resources',
      descAr: 'إدارة المستندات والمواد المالية',
      descEn: 'Manage financial documents and materials',
      icon: Package,
      route: '/dashboard/finance/cloudgm/customers/materials',
      color: '#8B5CF6',
      screenCode: 'CGM-CUST-FIN-03',
    },
    {
      id: 'create-invoice',
      titleAr: 'إنشاء فاتورة',
      titleEn: 'Create Invoice',
      descAr: 'إنشاء فاتورة جديدة مباشرة وإرسالها للعميل',
      descEn: 'Create a new invoice directly and send it to the customer',
      icon: FileText,
      route: '/dashboard/finance/create-invoice/cloudgm_customers',
      color: '#10b981',
      screenCode: 'CGM-CUST-INV-NEW',
      testId: 'card-create-invoice-customers',
    },
    {
      id: 'payments',
      titleAr: 'سجل المدفوعات الخاص بمنصة العملاء',
      titleEn: 'Customer Platform Payment Records',
      descAr: 'سجل شامل بكل الفواتير والمدفوعات المُنشأة لمنصة العملاء',
      descEn: 'Complete ledger of all invoices and payments created for the customer platform',
      icon: Wallet,
      route: '/dashboard/finance/cloudgm/customers/payments',
      color: '#3B82F6',
      screenCode: 'CGM-CUST-FIN-04',
      testId: 'card-payment-records-customers',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <DashboardLayout
      title="منصة العملاء (المالية)"
      titleEn="Customer Platform (Finance)"
      subtitle="الإدارة المالية لمنصة العملاء"
      subtitleEn="Financial management for customer platform"
      screenCode="FIN-CGM-CUST"
      backPath="/dashboard/finance/cloudgm"
      breadcrumb={[
        { labelAr: 'المالية', labelEn: 'Finance', path: '/dashboard/finance' },
        { labelAr: 'CLOUDGM FZ LLC', labelEn: 'CLOUDGM FZ LLC', path: '/dashboard/finance/cloudgm' },
        { labelAr: 'منصة العملاء (المالية)', labelEn: 'Customer Platform (Finance)', path: null }
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {visiblePages(items, 'route').map((item) => (
            <NavCard
              key={item.id}
              icon={item.icon}
              title={item.titleAr}
              titleEn={item.titleEn}
              description={item.descAr}
              descriptionEn={item.descEn}
              onClick={() => navigate(item.route)}
              color={item.color}
              screenCode={item.screenCode}
            />
          ))}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
